import type {MessagesStorageKey} from '@appManagers/appMessagesManager';
import type ChatBubbles from '@components/chat/bubbles';
import type ChatInput from '@components/chat/input';
import type Chat from '@components/chat/chat';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import Button from '@components/button';
import ButtonIcon from '@components/buttonIcon';
import CheckboxField from '@components/checkboxField';
import showDeleteMessagesPopup from '@components/popups/deleteMessages';
import showForwardPopup from '@components/popups/forward';
import {showSelectedMessagesReport} from '@components/popups/reportAd';
import SetTransition from '@components/singleTransition';
import getSelectionElementFromTarget from '@components/chat/getSelectionElementFromTarget';
import ListenerSetter from '@helpers/listenerSetter';
import showSendNowPopup from '@components/popups/sendNow';
import appNavigationController, {NavigationItem} from '@components/appNavigationController';
import {IS_MOBILE_SAFARI} from '@environment/userAgent';
import Modes from '@config/modes';
import I18n, {i18n, _i18n} from '@lib/langPack';
import findUpClassName from '@helpers/dom/findUpClassName';
import blurActiveElement from '@helpers/dom/blurActiveElement';
import cancelEvent from '@helpers/dom/cancelEvent';
import {getAppWindow} from '@helpers/appWindow';
import cancelSelection from '@helpers/dom/cancelSelection';
import getSelectedText from '@helpers/dom/getSelectedText';
import replaceContent from '@helpers/dom/replaceContent';
import type AppSearchSuper from '@components/appSearchSuper';
import isInDOM from '@helpers/dom/isInDOM';
import {randomLong} from '@helpers/random';
import {attachClickEvent, AttachClickOptions} from '@helpers/dom/clickEvent';
import findUpAsChild from '@helpers/dom/findUpAsChild';
import EventListenerBase from '@helpers/eventListenerBase';
import safeAssign from '@helpers/object/safeAssign';
import {AppManagers} from '@lib/managers';
import {attachContextMenuListener} from '@helpers/dom/attachContextMenuListener';
import appImManager from '@lib/appImManager';
import {Message} from '@layer';
import {makeFullMid} from '@appManagers/utils/messages/fullMid';
import flatten from '@helpers/array/flatten';
import IS_STANDALONE from '@environment/standalone';
import {toastNew} from '@components/toast';
import confirmationPopup from '@components/confirmationPopup';
import {
  expandAlbumSelectionRange,
  isSameGroupedSelectionUnit,
  setAlbumItemsSelection
} from '@components/chat/selectionRange';
import {ChatType} from './chatType';
import ChatInputPlate from '@components/chat/controlPlate';
import isSameMessageSelectionGroup, {getMessageSelectionGroup} from '@components/chat/isSameMessageSelectionGroup';

const accumulateMapSet = <T extends {size: number}>(map: Map<any, T>) => {
  return [...map.values()].reduce((acc, v) => acc + v.size, 0);
};

// const MIN_CLICK_MOVE = 32; // minimum bubble height

export class AppSelection extends EventListenerBase<{
  toggle: (isSelecting: boolean) => void
}> {
  public selectedMids: Map<PeerId, Set<number>> = new Map();
  public isSelecting = false;
  private selectionRoles = new WeakMap<HTMLElement, {role: string, tabIndex: string}>();

  public selectedText: string;

  protected listenerSetter: ListenerSetter;
  public isScheduled: boolean;
  protected listenElement: HTMLElement;

  protected onToggleSelection: (forwards: boolean, animate: boolean) => void | Promise<void>;
  protected onUpdateContainer: (cantForward: boolean, cantDelete: boolean, cantSend: boolean) => void;
  protected onCancelSelection: () => void;
  protected toggleByElement: (bubble: HTMLElement, selected?: boolean) => void;

  protected navigationType: NavigationItem['type'];

  protected getElementFromTarget: (target: HTMLElement) => HTMLElement;
  protected verifyTarget: (e: MouseEvent, target: HTMLElement) => boolean;
  protected verifyMouseMoveTarget: (e: MouseEvent, element: HTMLElement, selecting: boolean) => boolean;
  protected verifyTouchLongPress: () => boolean;
  protected targetLookupClassName: string;
  protected lookupBetweenParentClassName: string;
  protected lookupBetweenElementsQuery: string;

  protected doNotAnimate: boolean;
  protected managers: AppManagers;
  /**
   * How far the pointer has to travel before a press turns into a drag that selects what it runs
   * across. Without it the shake of an ordinary click counts as a drag, and the row it was meant to
   * toggle is done the other way round instead. Off by default - the elements of a chat and of the
   * search are selected one at a time, so their press has nothing else to be.
   */
  protected dragThreshold = 0;

  protected onTouchLongPress: (e: Event) => void;

  constructor(options: {
    managers: AppManagers,
    getElementFromTarget: AppSelection['getElementFromTarget'],
    verifyTarget?: AppSelection['verifyTarget'],
    verifyMouseMoveTarget?: AppSelection['verifyMouseMoveTarget'],
    verifyTouchLongPress?: AppSelection['verifyTouchLongPress'],
    targetLookupClassName: string,
    lookupBetweenParentClassName: string,
    lookupBetweenElementsQuery: string,
    onTouchLongPress?: AppSelection['onTouchLongPress']
  }) {
    super(false);

    safeAssign(this, options);

    this.navigationType = 'multiselect-' + randomLong() as any;
  }

  public attachListeners(listenElement: HTMLElement, listenerSetter: ListenerSetter) {
    if(this.listenElement) {
      this.listenerSetter.removeAll();
    }

    this.listenElement = listenElement;
    this.listenerSetter = listenerSetter;

    if(!listenElement) {
      if(this.navigationType) {
        appNavigationController.removeByType(this.navigationType);
      }
      return;
    }

    if(IS_TOUCH_SUPPORTED) {
      listenerSetter.add(listenElement)('touchend', () => {
        if(!this.isSelecting) return;
        this.selectedText = getSelectedText();
      });

      attachContextMenuListener({
        element: listenElement,
        callback: (e) => {
          if(
            this.isSelecting ||
            (e.target as HTMLElement).closest('reaction-element') ||
            (this.verifyTouchLongPress && !this.verifyTouchLongPress())
          ) return;

          this.onTouchLongPress?.(e);

          // * these two lines will fix instant text selection on iOS Safari
          document.body.classList.add('no-select'); // * need no-select on body because chat-input transforms in channels
          listenElement.addEventListener('touchend', (e) => {
            cancelEvent(e); // ! this one will fix propagation to document loader button, etc
            document.body.classList.remove('no-select');

            // this.chat.bubbles.onBubblesClick(e);
          }, {once: true, capture: true});

          if(IS_MOBILE_SAFARI && IS_STANDALONE) {
            listenElement.addEventListener('mousedown', cancelEvent, {once: true, capture: true});
          }

          cancelSelection();
          cancelEvent(e as any);
          const element = this.getElementFromTarget(e.target as HTMLElement);
          if(element) {
            this.toggleByElement(element);
          }
        },
        listenerSetter
      });

      return;
    }

    listenerSetter.add(listenElement)('mousedown', this.onMouseDown);
  }

  private onMouseDown = (e: MouseEvent) => {
    // console.log('selection mousedown', e);
    const element = findUpClassName(e.target, this.targetLookupClassName);
    if(e.button !== 0) {
      return;
    }

    if(this.verifyTarget && !this.verifyTarget(e, element)) {
      return;
    }

    // The drag-end `mouseup` and the post-drag click-swallow must land on whichever window the app is
    // in (the tab, or the Document PiP window) — otherwise a drag-select started in the PiP never ends
    // (mouseup fires on the PiP window, not the main document) and the selection sticks.
    const activeWindow = getAppWindow();
    const activeDocument = activeWindow.document;

    const {clientX: startX, clientY: startY} = e;
    const seen = new Map<string, HTMLElement>();
    let selecting: boolean;
    // * the first element the drag has actually processed — everything belonging to its selection
    // * unit is not a second element
    let dragAnchor: HTMLElement;

    /* let good = false;
    const {x, y} = e; */

    /* const bubbles = appImManager.bubbles;
    for(const mid in bubbles) {
      const bubble = bubbles[mid];
      bubble.addEventListener('mouseover', () => {
        console.log('mouseover');
      }, {once: true});
    } */

    let firstTarget = element;

    const processElement = (element: HTMLElement, checkBetween = true) => {
      const key = this.getKeyFromElement(element);
      if(key === undefined) return;

      if(!isInDOM(firstTarget)) {
        firstTarget = element;
      }

      if(seen.has(key)) {
        return;
      }

      if(dragAnchor && this.isSameSelectionUnit(dragAnchor, element)) {
        return;
      }

      const isSelected = this.isElementShouldBeSelected(element);
      if(selecting === undefined) {
        // bubblesContainer.classList.add('no-select');
        selecting = !isSelected;
      }

      seen.set(key, element);
      dragAnchor ??= element;

      if((selecting && !isSelected) || (!selecting && isSelected)) {
        const seenLength = seen.size;
        if(this.toggleByElement && checkBetween) {
          if(seenLength < 2) {
            if(findUpAsChild(element, firstTarget)) {
              firstTarget = element;
            }
          }

          const elementsBetween = this.getElementsBetween(firstTarget, element);
          // console.log(elementsBetween);
          if(elementsBetween.length) {
            elementsBetween.forEach((element) => {
              processElement(element, false);
            });
          }
        }

        if(!this.length()) {
          if(seenLength === 2 && this.toggleByElement) {
            for(const element of seen.values()) {
              this.toggleByElement(element, selecting);
            }
          }
        } else if(this.toggleByElement) {
          this.toggleByElement(element, selecting);
        }
      }
    };

    // const foundTargets: Map<HTMLElement, true> = new Map();
    let canceledSelection = false, travelled = !this.dragThreshold;
    const onMouseMove = (e: MouseEvent) => {
      if(!travelled) {
        if(Math.hypot(e.clientX - startX, e.clientY - startY) < this.dragThreshold) {
          return;
        }

        travelled = true;
      }

      if(!canceledSelection) {
        cancelSelection();
        canceledSelection = true;
        document.body.classList.add('no-select');
        // const chat = document.querySelector('.chat');
        // chat.classList.add('no-select');
      }
      /* if(!good) {
        if(Math.abs(e.x - x) > MIN_CLICK_MOVE || Math.abs(e.y - y) > MIN_CLICK_MOVE) {
          good = true;
        } else {
          return;
        }
      } */

      /* if(foundTargets.has(e.target as HTMLElement)) return;
      foundTargets.set(e.target as HTMLElement, true); */
      const element = this.getElementFromTarget(e.target as HTMLElement);
      if(!element) {
        // console.error('found no bubble', e);
        return;
      }

      if(this.verifyMouseMoveTarget && !this.verifyMouseMoveTarget(e, element, selecting)) {
        this.listenerSetter.removeManual(this.listenElement, 'mousemove', onMouseMove);
        this.listenerSetter.removeManual(activeDocument, 'mouseup', onMouseUp, documentListenerOptions);
        return;
      }

      processElement(element);
    };

    const onMouseUp = (e: MouseEvent) => {
      document.body.classList.remove('no-select');

      if(seen.size) {
        // * the click that ends a drag has to be swallowed, or whoever listens for it acts on the
        // * element the press started on - and toggles back what the drag has just done. It lands on
        // * the common ancestor of the press and the release rather than on either element, so
        // * `ignoreMove` is what lets this one through the moved-since-mousedown guard
        attachClickEvent(activeWindow, cancelEvent, {capture: true, once: true, passive: false, ignoreMove: true});
      }

      this.listenerSetter.removeManual(this.listenElement, 'mousemove', onMouseMove);
      // bubblesContainer.classList.remove('no-select');

      // ! CANCEL USER SELECTION !
      cancelSelection();
    };

    const documentListenerOptions = {once: true};
    this.listenerSetter.add(this.listenElement)('mousemove', onMouseMove);
    this.listenerSetter.add(activeDocument)('mouseup', onMouseUp, documentListenerOptions);
  };

  /**
   * Whether `element` is a part of the same selectable unit the drag has started on, and so must not
   * count as another element of the range
   */
  protected isSameSelectionUnit(anchor: HTMLElement, element: HTMLElement) {
    return false;
  }

  /**
   * What an element the drag runs over is held under, so it is passed over on the way back. One per
   * message here; a selection of something else (a chat list) answers in its own terms.
   */
  protected getKeyFromElement(element: HTMLElement): string {
    const mid = +element.dataset.mid;
    if(!mid || !element.dataset.peerId) {
      return undefined;
    }

    return makeFullMid(element.dataset.peerId.toPeerId(), mid);
  }

  /** What the element's checkbox is named after */
  protected getCheckboxName(element: HTMLElement) {
    return element.dataset.mid;
  }

  /** Drops everything the selection holds, out of wherever it is held */
  protected clearSelection() {
    this.selectedMids.clear();
  }

  protected getElementsBetween(first: HTMLElement, last: HTMLElement) {
    if(first === last) {
      return [];
    }

    const firstRect = first.getBoundingClientRect();
    const lastRect = last.getBoundingClientRect();
    const difference = (firstRect.top - lastRect.top) || (firstRect.left - lastRect.left);
    const isHigher = difference < 0;

    const parent = findUpClassName(first, this.lookupBetweenParentClassName);
    if(!parent) {
      return [];
    }

    const elements = Array.from(parent.querySelectorAll(this.lookupBetweenElementsQuery)) as HTMLElement[];
    let firstIndex = elements.indexOf(first);
    let lastIndex = elements.indexOf(last);

    if(!isHigher) {
      [lastIndex, firstIndex] = [firstIndex, lastIndex];
    }

    const slice = elements.slice(firstIndex + 1, lastIndex);

    // console.log('getElementsBetween', first, last, slice, firstIndex, lastIndex, isHigher);

    return slice;
  }

  protected isElementShouldBeSelected(element: HTMLElement) {
    return this.isMidSelected(element.dataset.peerId.toPeerId(), +element.dataset.mid);
  }

  protected appendCheckbox(element: HTMLElement, checkboxField: CheckboxField) {
    element.prepend(checkboxField.label);
  }

  public toggleElementCheckbox(element: HTMLElement, show: boolean) {
    const hasCheckbox = !!this.getCheckboxInputFromElement(element);
    if(show) {
      if(hasCheckbox) {
        return false;
      }

      const checkboxField = new CheckboxField({
        name: this.getCheckboxName(element),
        round: true
      });
      checkboxField.input.setAttribute('aria-label', element.getAttribute('aria-label') || element.textContent || I18n.format('Message.Context.Select', true));
      if(Modes.a11y && element.getAttribute('role') === 'button') {
        this.selectionRoles.set(element, {role: 'button', tabIndex: element.getAttribute('tabindex')});
        element.setAttribute('role', 'group');
        element.removeAttribute('tabindex');
      }

      // * if it is a render of new message
      if(this.isSelecting) { // ! avoid breaking animation on start
        if(this.isElementShouldBeSelected(element)) {
          checkboxField.input.checked = true;
          element.classList.add('is-selected');
        }
      }

      this.appendCheckbox(element, checkboxField);
    } else if(hasCheckbox) {
      this.getCheckboxInputFromElement(element).parentElement.remove();
      const previous = this.selectionRoles.get(element);
      if(previous) {
        element.setAttribute('role', previous.role);
        if(previous.tabIndex !== null) element.setAttribute('tabindex', previous.tabIndex);
        this.selectionRoles.delete(element);
      }
      this.toggleElementSelected(element, false);
    }

    return true;
  }

  protected getCheckboxInputFromElement(element: HTMLElement): HTMLInputElement {
    const field = element.firstElementChild;
    const input = field?.firstElementChild;
    if(!field?.classList.contains('checkbox-field') || !input?.classList.contains('checkbox-field-input')) {
      return;
    }

    return input as HTMLInputElement;
  }

  protected async updateContainer(forceSelection = false) {
    const size = this.selectedMids.size;
    if(!size && !forceSelection) return;

    let cantForward = !size;
    let cantDelete = !size;
    const cantSend = !size;

    for(const [peerId, mids] of this.selectedMids) {
      const storageKey = this.getStorageKey(peerId);
      const r = await this.managers.appMessagesManager.cantForwardDeleteMids(storageKey, Array.from(mids));
      cantForward ||= r.cantForward;
      cantDelete ||= r.cantDelete;

      if(cantForward && cantDelete) break;
    }

    this.onUpdateContainer?.(cantForward, cantDelete, cantSend);
  }

  private getStorageKey(peerId: PeerId): MessagesStorageKey {
    return `${peerId}_${this.isScheduled ? 'scheduled' : 'history'}`;
  }

  public getSelectedMids() {
    return flatten([...this.selectedMids.values()].map((set) => [...set])).sort((a, b) => a - b);
  }

  public getSelectedMessages() {
    const selectedMessagesPromises: Promise<Message.message | Message.messageService>[] = [];
    this.selectedMids.forEach((mids, peerId) => {
      const storageKey = this.getStorageKey(peerId);
      const p = Array.from(mids).map((mid) => this.managers.appMessagesManager.getMessageFromStorage(storageKey, mid));
      selectedMessagesPromises.push(...p);
    });
    return Promise.all(selectedMessagesPromises);
  }

  public toggleSelection(toggleCheckboxes = true, forceSelection = false) {
    const wasSelecting = this.isSelecting;
    const size = this.length();
    this.isSelecting = !!size || forceSelection;

    if(wasSelecting === this.isSelecting) return false;

    this.dispatchEvent('toggle', this.isSelecting);

    if(!IS_TOUCH_SUPPORTED) {
      this.listenElement.classList.toggle('no-select', this.isSelecting);

      if(wasSelecting) {
        // ! CANCEL USER SELECTION !
        cancelSelection();
      }
    }/*  else {
      if(!wasSelecting) {
        bubblesContainer.classList.add('no-select');
        setTimeout(() => {
          cancelSelection();
          bubblesContainer.classList.remove('no-select');
          cancelSelection();
        }, 100);
      }
    } */

    blurActiveElement();

    const forwards = !!size || forceSelection;
    const toggleResult = this.onToggleSelection?.(forwards, !this.doNotAnimate);

    if(!IS_MOBILE_SAFARI) {
      if(forwards) {
        appNavigationController.pushItem({
          type: this.navigationType,
          onPop: () => {
            this.cancelSelection();
          }
        });
      } else {
        appNavigationController.removeByType(this.navigationType);
      }
    }

    if(forceSelection) {
      (toggleResult || Promise.resolve()).then(() => this.updateContainer(forceSelection));
    }

    return true;
  }

  public cancelSelection = (doNotAnimate?: boolean) => {
    if(doNotAnimate) this.doNotAnimate = true;
    this.onCancelSelection?.();
    this.clearSelection();
    this.toggleSelection();
    cancelSelection();
    if(doNotAnimate) this.doNotAnimate = undefined;
  };

  public cleanup() {
    this.doNotAnimate = true;
    this.clearSelection();
    this.toggleSelection(false);
    this.doNotAnimate = undefined;
  }

  protected updateElementSelection(element: HTMLElement, isSelected: boolean) {
    this.toggleElementCheckbox(element, true);
    const input = this.getCheckboxInputFromElement(element);
    input.checked = isSelected;

    this.toggleSelection();
    this.updateContainer();
    this.toggleElementSelected(element, isSelected);
  }

  /**
   * Marks an element as one of the selected ones, and fades that mark in and out. `SetTransition`
   * says so with `animating`/`forwards`/`backwards` ON THE ELEMENT ITSELF, so a list whose elements
   * already speak those classes - a chat row does, for its muted state and its badges - marks them
   * some other way instead of letting this replay what it finds.
   */
  protected toggleElementSelected(element: HTMLElement, isSelected: boolean) {
    SetTransition({
      element,
      className: 'is-selected',
      forwards: isSelected,
      duration: 200
    });
  }

  public isMidSelected(peerId: PeerId, mid: number) {
    const set = this.selectedMids.get(peerId);
    return !!set?.has(mid);
  }

  public length() {
    return accumulateMapSet(this.selectedMids);
  }

  public toggleMid(peerId: PeerId, mid: number, unselect?: boolean) {
    let set = this.selectedMids.get(peerId);
    if(unselect || (unselect === undefined && set?.has(mid))) {
      if(set) {
        set.delete(mid);

        if(!set.size) {
          this.selectedMids.delete(peerId);
        }
      }
    } else {
      // const diff = rootScope.config.forwarded_count_max - this.length() - 1;
      // if(diff < 0) {
      //   toast(I18n.format('Chat.Selection.LimitToast', true));
      //   return false;
      //   /* const it = this.selectedMids.values();
      //   do {
      //     const mid = it.next().value;
      //     const mounted = this.appImManager.getMountedBubble(mid);
      //     if(mounted) {
      //       this.toggleByBubble(mounted.bubble);
      //     } else {
      //       const mids = this.appMessagesManager.getMidsByMid(mid);
      //       for(const mid of mids) {
      //         this.selectedMids.delete(mid);
      //       }
      //     }
      //   } while(this.selectedMids.size > MAX_SELECTION_LENGTH); */
      // }

      if(!set) {
        set = new Set();
        this.selectedMids.set(peerId, set);
      }

      set.add(mid);
    }

    return true;
  }

  /**
   * ! Call this method only to handle deleted messages
   */
  public deleteSelectedMids(peerId: PeerId, mids: number[], batch?: boolean) {
    const set = this.selectedMids.get(peerId);
    if(!set) {
      return;
    }

    mids.forEach((mid) => {
      set.delete(mid);
    });

    if(!set.size) {
      this.selectedMids.delete(peerId);
    }

    const after = () => {
      this.updateContainer();
      this.toggleSelection();
    };

    if(!batch) after();
    return after;
  }
}

export class SearchSelection extends AppSelection {
  protected selectionContainer: HTMLElement;
  protected selectionCountEl: HTMLElement;
  public selectionForwardBtn: HTMLElement;
  public selectionDeleteBtn: HTMLElement;
  public selectionGotoBtn: HTMLElement;

  private isPrivate: boolean;

  // * plate-scoped: the tab's listenerSetter outlives every selection session,
  // * so plate button listeners must not accumulate there
  private containerListenerSetter: ListenerSetter;

  constructor(
    private searchSuper: AppSearchSuper,
    managers: AppManagers,
    listenerSetter: ListenerSetter
  ) {
    super({
      managers,
      verifyTarget: (e, target) => !!target && this.isSelecting,
      getElementFromTarget: (target) => findUpClassName(target, 'search-super-item'),
      targetLookupClassName: 'search-super-item',
      lookupBetweenParentClassName: 'tabs-tab',
      lookupBetweenElementsQuery: '.search-super-item'
    });

    this.isPrivate = !searchSuper.showSender;
    !IS_TOUCH_SUPPORTED && this.attachListeners(searchSuper.container, listenerSetter);
  }

  /* public appendCheckbox(element: HTMLElement, checkboxField: CheckboxField) {
    checkboxField.label.classList.add('bubble-select-checkbox');

    if(element.classList.contains('document') || element.classList.contains('audio')) {
      element.querySelector('.document, .audio').append(checkboxField.label);
    } else {
      super.appendCheckbox(bubble, checkboxField);
    }
  } */

  public toggleSelection(toggleCheckboxes = true, forceSelection = false) {
    const ret = super.toggleSelection(toggleCheckboxes, forceSelection);

    if(ret && toggleCheckboxes) {
      const elements = Array.from(this.searchSuper.tabsContainer.querySelectorAll('.search-super-item')) as HTMLElement[];
      elements.forEach((element) => {
        this.toggleElementCheckbox(element, this.isSelecting);
      });
    }

    return ret;
  }

  public toggleByElement = (element: HTMLElement, selected?: boolean) => {
    const mid = +element.dataset.mid;
    const peerId = element.dataset.peerId.toPeerId();
    const isSelected = this.isMidSelected(peerId, mid);
    if(selected !== undefined && selected === isSelected) {
      return;
    }

    if(!this.toggleMid(peerId, mid)) {
      return;
    }

    this.updateElementSelection(element, this.isMidSelected(peerId, mid));
  };

  protected onUpdateContainer = (cantForward: boolean, cantDelete: boolean) => {
    const length = this.length();
    replaceContent(this.selectionCountEl, i18n('messages', [length]));
    this.selectionGotoBtn.classList.toggle('hide', length !== 1);
    this.selectionForwardBtn.classList.toggle('hide', cantForward);
    this.selectionDeleteBtn && this.selectionDeleteBtn.classList.toggle('hide', cantDelete);
  };

  protected onToggleSelection = (forwards: boolean, animate: boolean) => {
    SetTransition({
      element: this.searchSuper.navScrollableContainer,
      className: 'is-selecting',
      forwards,
      duration: animate ? 200 : 0,
      onTransitionEnd: () => {
        if(!this.isSelecting) {
          this.containerListenerSetter?.removeAll();
          this.containerListenerSetter = null;
          this.selectionContainer.remove();
          this.selectionContainer =
            this.selectionForwardBtn =
            this.selectionDeleteBtn =
            null;
          this.selectedText = undefined;
        }
      }
    });

    SetTransition({
      element: this.searchSuper.container,
      className: 'is-selecting',
      forwards,
      duration: 200
    });

    if(this.isSelecting) {
      if(!this.selectionContainer) {
        const BASE_CLASS = 'search-super-selection';
        this.selectionContainer = document.createElement('div');
        this.selectionContainer.classList.add(BASE_CLASS + '-container');

        this.containerListenerSetter = new ListenerSetter();

        const btnCancel = ButtonIcon(`close ${BASE_CLASS}-cancel`, {noRipple: true, ariaLabel: 'Cancel'});
        attachClickEvent(btnCancel, () => this.cancelSelection(), {listenerSetter: this.containerListenerSetter, once: true});

        this.selectionCountEl = document.createElement('div');
        this.selectionCountEl.classList.add(BASE_CLASS + '-count');

        const attachClickOptions: AttachClickOptions = {listenerSetter: this.containerListenerSetter};

        this.selectionGotoBtn = ButtonIcon(`message ${BASE_CLASS}-goto`, {ariaLabel: 'Message.Context.Goto'});
        attachClickEvent(this.selectionGotoBtn, () => {
          const peerId = [...this.selectedMids.keys()][0];
          const mid = [...this.selectedMids.get(peerId)][0];
          this.cancelSelection();

          appImManager.setInnerPeer({
            peerId,
            lastMsgId: mid,
            threadId: this.searchSuper.mediaTab.type === 'saved' ? this.searchSuper.searchContext.peerId : this.searchSuper.searchContext.threadId
          });
        }, attachClickOptions);

        this.selectionForwardBtn = ButtonIcon(`forward ${BASE_CLASS}-forward`, {ariaLabel: 'Forward'});
        attachClickEvent(this.selectionForwardBtn, () => {
          const obj: {[fromPeerId: PeerId]: number[]} = {};
          for(const [fromPeerId, mids] of this.selectedMids) {
            obj[fromPeerId] = Array.from(mids).sort((a, b) => a - b);
          }

          showForwardPopup(obj, () => {
            this.cancelSelection();
          });
        }, attachClickOptions);

        if(this.isPrivate) {
          this.selectionDeleteBtn = ButtonIcon(`delete danger ${BASE_CLASS}-delete`, {ariaLabel: 'Delete'});
          attachClickEvent(this.selectionDeleteBtn, () => {
            const peerId = this.searchSuper.searchContext.peerId;
            showDeleteMessagesPopup(
              peerId,
              this.getSelectedMids(),
              ChatType.Chat,
              () => {
                this.cancelSelection();
              }
            );
          }, attachClickOptions);
        }

        this.selectionContainer.append(...[
          btnCancel,
          this.selectionCountEl,
          this.selectionGotoBtn,
          this.selectionForwardBtn,
          this.selectionDeleteBtn
        ].filter(Boolean));

        const transitionElement = this.selectionContainer;
        transitionElement.style.opacity = '0';
        this.searchSuper.navScrollableContainer.append(transitionElement);

        void transitionElement.offsetLeft; // reflow
        transitionElement.style.opacity = '';
      }
    }
  };
}

export default class ChatSelection extends AppSelection {
  protected selectionInputWrapper: HTMLElement;
  protected selectionContainer: HTMLElement;
  protected selectionCountEl: HTMLElement;
  public selectionSendNowBtn: HTMLElement;
  public selectionForwardBtn: HTMLElement;
  public selectionDeleteBtn: HTMLElement;
  public selectionReportBtn: HTMLElement;

  // * "select messages to report" mode (entered on MESSAGE_ID_REQUIRED):
  // * the plate shows a "Report N Messages" action instead of forward/delete
  private reportSelectionData: {option: Uint8Array, text?: string};
  private selectionContainerForReport: boolean;

  // * plate-scoped: this.listenerSetter lives until peer change, so plate button
  // * listeners must not accumulate there across selection sessions
  private containerListenerSetter: ListenerSetter;

  public get isReportSelection() {
    return !!this.reportSelectionData;
  }

  constructor(
    private chat: Chat,
    private bubbles: ChatBubbles,
    private input: ChatInput,
    managers: AppManagers
  ) {
    super({
      managers,
      getElementFromTarget: getSelectionElementFromTarget,
      verifyTarget: (e, target) => {
        // LEFT BUTTON
        // проверка внизу нужна для того, чтобы не активировать селект если target потомок .bubble
        const bad = !this.selectedMids.size &&
          !(e.target as HTMLElement).classList.contains('bubble') &&
          !(e.target as HTMLElement).classList.contains('document-selection') &&
          target;

        return !bad;
      },
      verifyMouseMoveTarget: (e, element, selecting) => {
        const bad = e.target !== element &&
          !(e.target as HTMLElement).classList.contains('document-selection') &&
          selecting === undefined &&
          !this.selectedMids.size;
        return !bad;
      },
      verifyTouchLongPress: () => !this.chat.input.recording,
      targetLookupClassName: 'bubble',
      lookupBetweenParentClassName: 'bubbles-inner',
      lookupBetweenElementsQuery: '.bubble:not(.is-multiple-documents), .grouped-item',
      onTouchLongPress: () => {
        const {replySwipeHandler} = this.chat.bubbles;
        replySwipeHandler?.reset();
      }
    });
  }

  protected isSameSelectionUnit(anchor: HTMLElement, element: HTMLElement) {
    return isSameGroupedSelectionUnit(anchor, element);
  }

  protected getElementsBetween(first: HTMLElement, last: HTMLElement) {
    const elements = super.getElementsBetween(first, last);
    if(first === last) {
      // * there is no range yet — expanding the endpoints here would pull in the whole album
      return elements;
    }

    return expandAlbumSelectionRange({
      first,
      last,
      elements,
      getGroupedItems: (bubble) => this.bubbles.getBubbleGroupedItems(bubble)
    });
  }

  public appendCheckbox(bubble: HTMLElement, checkboxField: CheckboxField) {
    checkboxField.label.classList.add('bubble-select-checkbox');

    if(bubble.classList.contains('document-container')) {
      bubble.querySelector('.document, .audio').append(checkboxField.label);
    } else {
      super.appendCheckbox(bubble, checkboxField);
    }
  }

  public enterReportSelection(data: ChatSelection['reportSelectionData']) {
    if(this.isSelecting) {
      // * an unrelated active selection must not become the report payload — start fresh
      this.cancelSelection(true);
    }

    this.reportSelectionData = data;
    if(!this.toggleSelection(true, true)) {
      // * the previous plate can survive the synchronous cancel — rebuild it for the report mode
      Promise.resolve(this.onToggleSelection(true, !this.doNotAnimate))
      .then(() => this.updateContainer(true));
    }
  }

  public cleanup() {
    this.reportSelectionData = undefined;
    super.cleanup();
  }

  protected async updateContainer(forceSelection = false) {
    if(this.isReportSelection) {
      // * no forward/delete state to compute — only the count matters here
      this.onUpdateContainer(true, true, true);
      return;
    }

    return super.updateContainer(forceSelection);
  }

  public toggleSelection(toggleCheckboxes = true, forceSelection = false) {
    // * while choosing messages to report, the selection survives reaching zero
    // * selected — the mode ends only via cancel or a sent report
    const ret = super.toggleSelection(toggleCheckboxes, forceSelection || this.isReportSelection);

    if(ret && !this.isSelecting) {
      this.reportSelectionData = undefined;
      this.refreshSelectionGroup();
    }

    if(ret && toggleCheckboxes) {
      const history = this.bubbles.getRenderedHistory('asc');
      for(const fullMid of history) {
        if(this.bubbles.skippedMids.has(fullMid)) {
          continue;
        }

        const bubble = this.bubbles.getBubble(fullMid);
        this.toggleElementCheckbox(bubble, this.isSelecting);
      }
    }

    return ret;
  }

  public toggleElementCheckbox(bubble: HTMLElement, show: boolean) {
    if(show && !this.canSelectBubble(bubble)) return;

    const ret = super.toggleElementCheckbox(bubble, show);
    if(ret) {
      const isGrouped = bubble.classList.contains('is-grouped');
      if(isGrouped) {
        this.bubbles.getBubbleGroupedItems(bubble).forEach((item) => this.toggleElementCheckbox(item, show));
      }
    }

    return ret;
  }

  public toggleByElement = (bubble: HTMLElement, selected?: boolean): Promise<void> => {
    if(!this.canSelectBubble(bubble)) return;

    const mid = +bubble.dataset.mid;
    const peerId = bubble.dataset.peerId.toPeerId();

    const isGrouped = bubble.classList.contains('is-grouped');
    if(isGrouped) {
      if(selected !== undefined && setAlbumItemsSelection({
        album: bubble,
        selected,
        getGroupedItems: (album) => this.bubbles.getBubbleGroupedItems(album),
        setElementSelection: (element, selected) => this.toggleByElement(element, selected)
      })) {
        return;
      }

      if(!this.isGroupedBubbleSelected(bubble)) {
        const set = this.selectedMids.get(peerId);
        if(set) {
          // const mids = await this.chat.getMidsByMid(mid);
          const mids = this.getMidsFromGroupContainer(bubble);
          mids.forEach(({mid}) => set.delete(mid));
        }
      }

      /* const promises =  */this.bubbles.getBubbleGroupedItems(bubble).map((item) => this.toggleByElement(item));
      // await Promise.all(promises);
      return;
    }

    const isSelected = this.isMidSelected(peerId, mid);
    if(selected !== undefined && selected === isSelected) {
      return;
    }

    if(!this.toggleMid(peerId, mid)) {
      return;
    }
    this.refreshSelectionGroup();

    const isGroupedItem = bubble.classList.contains('grouped-item');
    if(isGroupedItem) {
      const groupContainer = findUpClassName(bubble, 'bubble');
      const isGroupedSelected = this.isGroupedBubbleSelected(groupContainer);
      const isGroupedMidsSelected = this.isGroupedMidsSelected(groupContainer);

      const willChange = isGroupedMidsSelected || isGroupedSelected;
      if(willChange) {
        this.updateElementSelection(groupContainer, isGroupedMidsSelected);
      }
    }

    this.updateElementSelection(bubble, this.isMidSelected(peerId, mid));
  };

  public isElementShouldBeSelected(element: HTMLElement) {
    const isGrouped = element.classList.contains('is-grouped');
    return super.isElementShouldBeSelected(element) && (!isGrouped || this.isGroupedMidsSelected(element));
  }

  protected isGroupedBubbleSelected(bubble: HTMLElement) {
    const groupedCheckboxInput = this.getCheckboxInputFromElement(bubble);
    return groupedCheckboxInput?.checked;
  }

  protected getMidsFromGroupContainer(groupContainer: HTMLElement) {
    const elements = this.chat.bubbles.getBubbleGroupedItems(groupContainer);
    if(!elements.length) {
      elements.push(groupContainer);
    }

    return elements.map((element) => {
      return {
        mid: +element.dataset.mid,
        peerId: element.dataset.peerId.toPeerId()
      }
    });
  }

  protected isGroupedMidsSelected(groupContainer: HTMLElement) {
    const mids = this.getMidsFromGroupContainer(groupContainer);
    const selectedMids = mids.filter(({peerId, mid}) => this.isMidSelected(peerId, mid));
    return mids.length === selectedMids.length;
  }

  protected getCheckboxInputFromElement(bubble: HTMLElement) {
    return bubble.classList.contains('document-container') ?
      bubble.querySelector('.bubble-select-checkbox > .checkbox-field-input') as HTMLInputElement :
      super.getCheckboxInputFromElement(bubble);
  }

  public canSelectBubble(bubble: HTMLElement) {
    return bubble &&
      // * a service message IS selectable (it can be deleted just like a regular one) — only the
      // * bubbles that stand for no message at all are not: date separators, and the admin log,
      // * which is a read-only view whose entries live outside the message storage
      !bubble.classList.contains('is-date') &&
      this.chat.type !== ChatType.Logs &&
      // * welcome messages are templates, not the chat's messages: there is nothing to forward, and
      // * the few there are get edited and deleted one by one from their menu
      this.chat.type !== ChatType.Welcome &&
      // * a report is about what someone posted, and a service message is not that — tdesktop
      // * rules them out of the choose-messages flow too (HistoryItem::suggestReport)
      !(this.isReportSelection && bubble.classList.contains('service')) &&
      !bubble.classList.contains('is-outgoing') &&
      !bubble.classList.contains('is-error') &&
      !bubble.classList.contains('bubble-first') &&
      !bubble.classList.contains('avoid-selection') &&
      isSameMessageSelectionGroup(
        this.selectedMids,
        bubble.classList.contains('is-ephemeral')
      );
  }

  public refreshSelectionGroup() {
    const isEphemeral = getMessageSelectionGroup(this.selectedMids);

    const groups = new Map<HTMLElement, boolean>();
    const history = this.bubbles.getRenderedHistory('asc');
    for(const fullMid of history) {
      if(this.bubbles.skippedMids.has(fullMid)) {
        continue;
      }

      const bubble = this.bubbles.getBubble(fullMid);
      if(!bubble) {
        continue;
      }

      const isCompatible = isEphemeral === undefined ||
        isEphemeral === bubble.classList.contains('is-ephemeral');
      bubble.classList.toggle('selection-group-incompatible', !isCompatible);
      if(this.isSelecting) {
        this.toggleElementCheckbox(bubble, isCompatible);
      }

      const group = findUpClassName(bubble, 'bubbles-group');
      if(group) {
        groups.set(group, !!groups.get(group) || isCompatible);
      }
    }

    for(const [group, hasCompatibleBubble] of groups) {
      group.classList.toggle('selection-group-incompatible', !hasCompatibleBubble);
    }
  }

  protected onToggleSelection = async(forwards: boolean, animate: boolean) => {
    // Every plate is the same width now, so this just cross-fades.
    await this.chat.input.center(animate);

    SetTransition({
      element: this.input.chatInput,
      className: 'is-selecting',
      forwards,
      duration: animate ? 200 : 0
    });

    SetTransition({
      element: this.listenElement,
      className: 'is-selecting',
      forwards,
      duration: animate ? 200 : 0,
      onTransitionEnd: () => {
        if(!this.isSelecting) {
          this.removeSelectionContainer();
          this.selectedText = undefined;
        }
      }
    });

    // * the plate layout depends on the mode — rebuild it if the mode has changed
    if(
      this.isSelecting &&
      this.selectionContainer &&
      this.selectionContainerForReport !== !!this.isReportSelection
    ) {
      this.removeSelectionContainer();
    }

    if(this.isSelecting && !this.selectionContainer) {
      this.selectionContainerForReport = !!this.isReportSelection;
      this.selectionInputWrapper = document.createElement('div');
      this.selectionInputWrapper.classList.add('chat-input-wrapper', 'selection-wrapper');

      this.containerListenerSetter = new ListenerSetter();
      const attachClickOptions: AttachClickOptions = {listenerSetter: this.containerListenerSetter};

      this.selectionCountEl = document.createElement('div');
      this.selectionCountEl.classList.add('selection-container-count');

      if(this.isReportSelection) {
        // * report-selection mode (tdesktop's choose-for-report): cancel on the left,
        // * a "Report N Messages" action in the centre, nothing on the right
        const cancelBtn = ButtonIcon('close selection-container-close', {ariaLabel: 'Cancel'});
        attachClickEvent(cancelBtn, () => this.cancelSelection(), attachClickOptions);

        this.selectionReportBtn = Button('btn-primary btn-transparent text-bold chat-input-plate-button selection-container-report');
        this.selectionReportBtn.append(this.selectionCountEl);
        attachClickEvent(this.selectionReportBtn, () => {
          const mids = this.getSelectedMids();
          const data = this.reportSelectionData;
          if(!mids.length || !data) {
            return;
          }

          showSelectedMessagesReport(this.chat.peerId, mids, data.option, data.text, () => {
            this.cancelSelection();
          });
        }, attachClickOptions);

        this.selectionContainer = ChatInputPlate({
          class: 'selection-container',
          left: cancelBtn,
          center: this.selectionReportBtn
        }) as HTMLElement;
      } else {
        // Centre slot — the "N selected" count, styled as a transparent button;
        // tapping it clears the selection.
        const countButton = Button('btn-primary btn-transparent text-bold chat-input-plate-button');
        countButton.append(this.selectionCountEl);
        attachClickEvent(countButton, () => this.cancelSelection(), attachClickOptions);

        // Left slot — delete.
        this.selectionDeleteBtn = ButtonIcon('delete danger selection-container-delete', {ariaLabel: 'Delete'});
        attachClickEvent(this.selectionDeleteBtn, () => {
          showDeleteMessagesPopup(
            this.chat.peerId,
            this.getSelectedMids(),
            this.chat.type,
            () => {
              this.cancelSelection();
            }
          );
        }, attachClickOptions);

        // Right slot — forward (or "send now" for scheduled messages).
        let rightButton: HTMLElement;
        if(this.chat.type === ChatType.Scheduled) {
          rightButton = this.selectionSendNowBtn = ButtonIcon('send2 selection-container-send', {ariaLabel: 'MessageScheduleSend'});
          attachClickEvent(this.selectionSendNowBtn, () => {
            showSendNowPopup(this.chat.peerId, [...this.selectedMids.get(this.chat.peerId)], () => {
              this.cancelSelection();
            });
          }, attachClickOptions);
        } else {
          rightButton = this.selectionForwardBtn = ButtonIcon('forward selection-container-forward', {ariaLabel: 'Forward'});
          attachClickEvent(this.selectionForwardBtn, () => {
            const obj: {[fromPeerId: PeerId]: number[]} = {};
            for(const [fromPeerId, mids] of this.selectedMids) {
              obj[fromPeerId] = Array.from(mids).sort((a, b) => a - b);
            }

            showForwardPopup(obj, () => {
              this.cancelSelection();
            });
          }, attachClickOptions);
        }

        this.selectionContainer = ChatInputPlate({
          class: 'selection-container',
          left: this.selectionDeleteBtn,
          center: countButton,
          right: rightButton
        }) as HTMLElement;
      }

      this.selectionInputWrapper.style.opacity = '0';
      this.selectionInputWrapper.append(this.selectionContainer);
      this.input.inputContainer.append(this.selectionInputWrapper);

      void this.selectionInputWrapper.offsetLeft; // reflow
      this.selectionInputWrapper.style.opacity = '';
    }
  };

  protected onUpdateContainer = (cantForward: boolean, cantDelete: boolean, cantSend: boolean) => {
    if(!this.selectionCountEl) {
      return;
    }

    const length = this.length();
    if(this.selectionContainerForReport) {
      replaceContent(
        this.selectionCountEl,
        length ? i18n('Report2MessagesCount', [length]) : i18n('Chat.Menu.SelectMessages')
      );
    } else {
      replaceContent(this.selectionCountEl, i18n('messages', [length]));
    }

    this.selectionSendNowBtn?.toggleAttribute('disabled', cantSend);
    this.selectionForwardBtn?.toggleAttribute('disabled', cantForward);
    this.selectionDeleteBtn?.toggleAttribute('disabled', cantDelete);
    this.selectionReportBtn?.toggleAttribute('disabled', !length);
  };

  private removeSelectionContainer() {
    this.containerListenerSetter?.removeAll();
    this.containerListenerSetter = null;
    this.selectionInputWrapper.remove();
    this.selectionInputWrapper =
      this.selectionContainer =
      this.selectionCountEl =
      this.selectionSendNowBtn =
      this.selectionForwardBtn =
      this.selectionDeleteBtn =
      this.selectionReportBtn =
      null;
    this.selectionContainerForReport = undefined;
  }

  protected onCancelSelection = async() => {
    this.reportSelectionData = undefined;
    // return;
    // const promises: Promise<HTMLElement>[] = [];
    // for(const [peerId, mids] of this.selectedMids) {
    //   for(const mid of mids) {
    //     promises.push(this.bubbles.getMountedBubble(mid).then((m) => m?.bubble));
    //   }
    // }

    // const bubbles = filterUnique((await Promise.all(promises)).filter(Boolean));
    // bubbles.forEach((bubble) => {
    //   this.toggleByElement(bubble);
    // });
  };
}
